<template>
  <vf-carousel
    v-if="items.length"
    :class-container="{ [`lg:grid lg:cols-2 lg:overflow-visible ${brandClasses.carousel}`]: alwaysUseGrid || items?.length > 1 }"
    data-test-id="vf-media-gallery"
    :loop="gallery.loop"
    @paginate="handleChange"
    @scroll-start="handleChange"
    @to-next="handleChange"
    @to-prev="handleChange"
  >
    <template #default="{ activeItem, action }">
      <div
        v-for="(item, i) in items"
        :key="item.src"
        class="group relative w-full"
        :class="[getItemClass ? getItemClass(i) : { [brandClasses.carouselItemFirst || '']: i === 0 }, brandClasses.carouselItem]"
      >
        <template v-if="item.type === 'image'">
          <vf-zoomer
            v-if="item.srcZoom"
            ref="zoomers"
            :alt="isAltTextRepeated ? `${item.alt} - ${i + 1}` : item.alt"
            :height="presets.height"
            :props-img="{ class: 'w-full' }"
            :src="getImageSrc(item)"
            :src-zoom="item.srcZoom"
            :width="presets.width"
            @swipe="action(activeItem - $event)"
            @zoom="$zoomFactor => $emit('zoom', i, $zoomFactor)"
          />
          <base-picture
            v-else
            :alt="isAltTextRepeated ? `${item.alt} - ${i + 1}` : item.alt"
            class="w-full"
            :height="presets.height"
            :lazy="i >= 4"
            :src="getImageSrc(item)"
            :width="presets.width"
          />

          <slot v-bind="{ i }" />

          <base-button
            v-if="$feature.showShopSimilar && i === 0 && syteReady"
            class="syte-discovery absolute bottom-0 left-0 z-2 mb-2 ml-2 flex focus:op-100"
            :data-image-src="item.src"
            data-placement="PDP-main"
            data-test-id="vf-media-gallery-shop-similar"
            :sku="productId"
          >
            <vf-icon name="objective" size="md" />
            <span class="ml-2 text-md ">
              {{ $t.shopSimilar }}
            </span>
          </base-button>
        </template>

        <vf-video
          v-if="item.type === 'video'"
          ref="videos"
          v-bind="item"
          class="full bg-black"
          :controlslist
          :fit="gallery.videoFit"
          @play="handlePlay(item)"
        />

        <sketchfab v-if="item.type === 'sketchfab' && enableSketchfab" :product-id="item.productId" />
      </div>
    </template>

    <template #prev="{ active, action }">
      <base-button
        :aria-label="$t.previous"
        class="absolute left-0 top-1/2 z-2 duration -mt-4 lg:hidden -scale-x-100"
        :class="[{ 'pointer-events-none op-0': !gallery.loop && !active, 'hidden': !showPrevNext || items.length < 2 }]"
        data-test-id="vf-carousel-arrow-left"
        @click.stop="action"
      >
        <vf-icon name="chevron" size="lg" />
      </base-button>
    </template>

    <template #next="{ active, action }">
      <base-button
        :aria-label="$t.next"
        class="absolute right-0 top-1/2 z-2 duration -mt-4 lg:hidden"
        :class="[{ 'pointer-events-none op-0': !gallery.loop && !active, 'hidden': !showPrevNext || items.length < 2 }]"
        data-test-id="vf-carousel-arrow-right"
        @click.stop="action"
      >
        <vf-icon name="chevron" size="lg" />
      </base-button>
    </template>

    <template v-if="showPagination" #pagination="{ activeItem, action, pages }">
      <base-scroll
        v-if="thumbnails"
        class-container="flex pt-4 space-x-2 lg:hidden wrap"
        data-test-id="vf-media-gallery-pagination"
      >
        <div
          v-for="(item, i) in items"
          :key="i"
          :aria-current="i === activeItem"
          class="w-12 flex cursor-pointer center b b-transparent"
          :class="{ '!b-grey-10': i === activeItem, 'relative': item.type === 'video' }"
          :style="{ 'aspect-ratio': gallery.presets.src.width / gallery.presets.src.height }"
          @click="action(i)"
        >
          <base-picture :src="item.type === 'video' ? item.poster! : item.src!" />
          <vf-icon v-if="item.type === 'video'" class="absolute" name="play" />
        </div>
      </base-scroll>
      <vf-media-gallery-pagination
        v-else-if="pagination && pages"
        :active-page="activeItem"
        :class="brandClasses.pagination"
        :pages
      />
    </template>
  </vf-carousel>
  <div v-else class="bg-grey-80 py-1/2 text-center lh-0">
    {{ $t.imageUnavailable }}
  </div>
</template>

<script lang="ts" setup>
import type { BaseVideo as BaseVideoType, VfZoomer as VfZoomerType } from '#components'
import type { Responsive } from '#types/common'
import type { ImageItem, SketchfabItem, VideoItem } from '#types/media'

const { items } = defineProps<{
  /**
   * List of configs that will be used to render slides and thumbnails
   */
  items: (ImageItem | VideoItem | SketchfabItem)[]
  /**
   * Defines whether pagination dots should be displayed under carousel. Tablet/Mobile only
   */
  pagination?: boolean
  /**
   * Defines whether thumbnails should be displayed under carousel. This property is an alternative for "pagination" and has higher priority. Tablet/Mobile only
   */
  thumbnails?: boolean
  productId?: string
  nativePinchZoom?: boolean
  getItemClass?: (index: number) => string
}>()

defineEmits<{
  zoom: [index: number, zoomFactor: number]
}>()

const {
  alwaysUseGrid,
  brandClasses,
  controlslist,
  showPagination,
  showPrevNext
} = useAppConfig().components.vf.mediaGallery
const { enableSketchfab, gallery } = useAppConfig().pages?.pdp || {}
const { $feature, $viewport } = useNuxtApp()
const { init: initSyte, ready: syteReady } = useSyte()
const imageSource = computed(() => getValueForBreakpoint($viewport.breakpoint, gallery.imageSource) || 'src')

const isAltTextRepeated = computed(() => items.length > 1 && items.every((item) => item.alt === items[0].alt))
const presets = computed(
  () => alwaysUseGrid || items.length > 1 ? gallery.presets.src : gallery.presets[imageSource.value]
)

const getImageSrc = (item) => {
  return Object.fromEntries(
    Object.entries(gallery.imageSource).map(([breakpoint, key]) => [
      breakpoint,
      items.length === 1 ? item[key as keyof typeof item] || item.src : item.src
    ])
  ) as Responsive<string>
}

if ($feature.showShopSimilar) initSyte()

const videos = ref<typeof BaseVideoType[]>([])
const zoomers = ref<typeof VfZoomerType[]>([])

const handleChange = () => {
  videos.value.forEach((video) => video.pause())
  zoomers.value.forEach((zoomer) => zoomer.resetZoom())
}

const handlePlay = ({ src }) => {
  const resolvedSrc = getValueForBreakpoint($viewport.breakpoint, src)
  const startingVideo = videos.value.find(
    ({ videoRef }) => getValueForBreakpoint($viewport.breakpoint, videoRef.src) === resolvedSrc
  )
  if (startingVideo && !startingVideo.videoRef.el.muted) {
    videos.value
      .filter((video) => video !== startingVideo && !video.videoRef.el.muted)
      .forEach((video) => video.pause())
  }
}
</script>
